import { Link } from 'react-router-dom';
import { SEO } from '../../utils/seo';

export default function TermsPage() {
  return (
    <>
      <SEO
        title="Terms of Service | SALL Engineering"
        description="Terms of Service for SALL Engineering. Read our terms and conditions for using our services."
        keywords="terms of service, terms and conditions, SALL Engineering"
      />
      
      <div className="min-h-screen bg-white">
        {/* Navigation */}
        <nav className="bg-white shadow-md">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <img src="https://public.readdy.ai/ai/img_res/9627a3c2-cdf6-4a10-bcaf-5daa34df9421.png" alt="SALL Engineering" className="h-12 w-auto" />
            </Link>
            <Link to="/" className="text-sm font-medium text-gray-900 hover:text-teal-600 transition-colors cursor-pointer">
              Back to Home
            </Link>
          </div>
        </nav>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-6 py-20">
          <h1 className="text-5xl font-bold text-gray-900 mb-8">Terms of Service</h1>
          <p className="text-sm text-gray-500 mb-12">Last Updated: January 2025</p>

          <div className="prose prose-lg max-w-none">
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">1. Agreement to Terms</h2>
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                By accessing or using the website and services of SALL Engineering, a division of SALL Collection Tekstil Sanayi ve Ticaret A.Ş. ("Company," "we," "our," or "us"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our website or services.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">2. Description of Services</h2>
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                SALL Engineering provides renewable energy engineering and consulting services, including but not limited to:
              </p>
              <ul className="list-disc pl-6 mb-4 text-base text-gray-700 space-y-2">
                <li>Solar energy system design and engineering</li>
                <li>Wind power solutions and analysis</li>
                <li>Battery energy storage system integration</li>
                <li>Grid integration and power system studies</li>
                <li>Technical consulting and project management</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">3. Use of Website</h2>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.1 Permitted Use</h3>
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                You may use our website for lawful purposes only. You agree not to:
              </p>
              <ul className="list-disc pl-6 mb-4 text-base text-gray-700 space-y-2">
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe upon the rights of others</li>
                <li>Transmit any harmful or malicious code</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Interfere with the proper functioning of the website</li>
                <li>Use automated systems to access the website without permission</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">3.2 Account Responsibility</h3>
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                If you create an account on our website, you are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">4. Intellectual Property Rights</h2>
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                All content on this website, including text, graphics, logos, images, and software, is the property of SALL Engineering or its licensors and is protected by intellectual property laws. You may not reproduce, distribute, modify, or create derivative works without our express written permission.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">5. Professional Services</h2>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">5.1 Service Agreements</h3>
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                Professional engineering and consulting services are provided under separate written agreements. These Terms do not constitute a service agreement or create any professional relationship.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">5.2 No Warranties</h3>
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                Information provided on this website is for general informational purposes only and does not constitute professional advice. We make no warranties regarding the accuracy, completeness, or reliability of any information on the website.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">6. Limitation of Liability</h2>
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                To the fullest extent permitted by law, SALL Engineering shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from:
              </p>
              <ul className="list-disc pl-6 mb-4 text-base text-gray-700 space-y-2">
                <li>Your use or inability to use our website or services</li>
                <li>Any unauthorized access to or use of our servers</li>
                <li>Any interruption or cessation of transmission to or from our website</li>
                <li>Any bugs, viruses, or similar harmful components</li>
                <li>Any errors or omissions in any content</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">7. Indemnification</h2>
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                You agree to indemnify, defend, and hold harmless SALL Engineering, its affiliates, officers, directors, employees, and agents from any claims, liabilities, damages, losses, and expenses arising out of or related to your use of the website or violation of these Terms.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">8. Third-Party Links and Content</h2>
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                Our website may contain links to third-party websites or services that are not owned or controlled by SALL Engineering. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party websites or services.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">9. Modifications to Services and Terms</h2>
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                We reserve the right to modify or discontinue our website or services at any time without notice. We may also revise these Terms at any time by updating this page. Your continued use of the website after any changes constitutes acceptance of the new Terms.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">10. Termination</h2>
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                We may terminate or suspend your access to our website immediately, without prior notice or liability, for any reason, including if you breach these Terms. Upon termination, your right to use the website will immediately cease.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">11. Governing Law and Jurisdiction</h2>
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                These Terms shall be governed by and construed in accordance with the laws of the Republic of Türkiye, without regard to its conflict of law provisions. Any disputes arising from these Terms or your use of the website shall be subject to the exclusive jurisdiction of the courts of Istanbul, Türkiye.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">12. Severability</h2>
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">13. Entire Agreement</h2>
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                These Terms constitute the entire agreement between you and SALL Engineering regarding your use of the website and supersede all prior agreements and understandings.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">14. Contact Information</h2>
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-base text-gray-700 mb-2"><strong>SALL Engineering</strong></p>
                <p className="text-base text-gray-700 mb-2">A division of SALL Collection Tekstil Sanayi ve Ticaret A.Ş.</p>
                <p className="text-base text-gray-700 mb-2">Istanbul, Türkiye</p>
                <p className="text-base text-gray-700">Email: info@sallengineering.com</p>
              </div>
            </section>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-gray-100 py-8">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <p className="text-sm text-gray-600">© 2025 SALL Engineering. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </>
  );
}
